import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dumbbell, Calendar, Clock, User, MapPin, CheckCircle2, XCircle, AlertTriangle, Loader2, House } from 'lucide-react';

export default function AlunoTreinosPage() {
  const [treinos, setTreinos] = useState([]);
  const [inadimplente, setInadimplente] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmando, setConfirmando] = useState(null);

  const fetchTreinos = useCallback(async () => {
    try {
      const res = await api.get('/aluno/treinos');
      setTreinos(res.data.treinos || []);
      setInadimplente(res.data.inadimplente || false);
    } catch { toast.error('Erro ao carregar treinos'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTreinos(); }, [fetchTreinos]);

const confirmar = async (treinoId) => {
  setConfirmando(treinoId);
  try {
    await api.post(`/aluno/treinos/${treinoId}/confirmar`);
    toast.success('Presença confirmada!');
    fetchTreinos();
  } catch (err) {
    toast.error(formatApiError(err));
  }
  setConfirmando(null);
};

  const cancelar = async (treinoId) => {
    setConfirmando(treinoId);
    try {
      await api.delete(`/aluno/treinos/${treinoId}/confirmar`);
      toast.success('Confirmacao cancelada');
      fetchTreinos();
    } catch (err) { toast.error(formatApiError(err)); }
    setConfirmando(null);
  };

const futuros = treinos
  .filter(t => !t.encerrado)
  .sort((a, b) => {
    const dataA = new Date(a.data + ' ' + a.horario);
    const dataB = new Date(b.data + ' ' + b.horario);
    return dataA - dataB;
  });

const passados = treinos
  .filter(t => t.encerrado)
  .sort((a, b) => {
    const dataA = new Date(a.data + ' ' + a.horario);
    const dataB = new Date(b.data + ' ' + b.horario);
    return dataB - dataA;
  });

if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48 bg-[#141414]" />
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-28 bg-[#141414]" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="aluno-treinos-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--cor-primaria, #007AFF) 15%, transparent)' }}>
          <Dumbbell className="w-5 h-5" style={{ color: 'var(--cor-primaria, #007AFF)' }} />
        </div>
        <div>
          <h1 className="text-2xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">MEUS TREINOS</h1>
          <p className="text-sm text-zinc-500">{futuros.length} treino(s) proximo(s)</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 p-1 bg-[#0F0F0F] border border-[#27272A] rounded-xl">
        <div
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: 'var(--cor-primaria, #007AFF)' }}
        >
          <Dumbbell className="w-4 h-4" />
          Presencial
        </div>

        <Link
          to="/aluno/treinos-casa"
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-zinc-400 hover:text-white hover:bg-[#181818] transition-colors"
        >
          <House className="w-4 h-4" />
          Em Casa
        </Link>
      </div>

      {inadimplente && (
        <Card className="bg-red-500/5 border-red-500/20" data-testid="treinos-inadimplente-alert">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
            <div>
              <p className="text-sm font-bold text-red-400">Voce esta inadimplente</p>
              <p className="text-xs text-zinc-400">Regularize sua situacao financeira para poder confirmar presença nos treinos.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {futuros.length === 0 && passados.length === 0 && (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-8 text-center">
            <Dumbbell className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">Nenhum treino encontrado</p>
          </CardContent>
        </Card>
      )}

      {futuros.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm tracking-[0.15em] uppercase font-bold text-zinc-500">Proximos Treinos</h2>
          {futuros.map((t, index) => (
            <TreinoCard key={t.id} treino={t} inadimplente={inadimplente} onConfirmar={confirmar} onCancelar={cancelar} confirmando={confirmando} isFuturo podeConfirmar={index ===0} />
          ))}
        </div>
      )}

      {passados.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm tracking-[0.15em] uppercase font-bold text-zinc-500">Treinos Anteriores</h2>
          {passados.map(t => (
            <TreinoCard key={t.id} treino={t} inadimplente={inadimplente} onConfirmar={confirmar} onCancelar={cancelar} confirmando={confirmando} isFuturo={false} />
          ))}
        </div>
      )}
    </div>
  );
}

function TreinoCard({ treino: t, inadimplente, onConfirmar, onCancelar, confirmando, isFuturo, podeConfirmar = true }) {
  const isLoading = confirmando === t.id;
  const encerrado = t.encerrado;
const formatarDataBR = (data) => {
  if (!data) return '';
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}/${ano}`;
};

  return (
    <Card className={`border transition-all duration-200 ${encerrado ? 'bg-[#0F0F0F] border-[#1e1e1e] opacity-70' : 'bg-[#141414] border-[#27272A] hover:border-[#3f3f46]'}`} data-testid={`treino-card-${t.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-base font-bold text-white truncate">{t.nome}</h3>
              {encerrado && <Badge className="bg-zinc-700/30 text-zinc-500 border-zinc-700/40 text-xs">Encerrado</Badge>}
              {t.presenca === true && <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">Presente</Badge>}
              {t.presenca === false && <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">Falta</Badge>}
              {t.confirmado && !encerrado && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs" data-testid={`badge-confirmado-${t.id}`}>Eu vou</Badge>}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatarDataBR(t.data)}
              </span>

              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {t.horario}
                {t.horario_fim ? ` às ${t.horario_fim}` : ''}
              </span>

              {t.local && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {t.local}
                </span>
              )}

              {t.turma_nome && (
                <span className="flex items-center gap-1">
                  <Dumbbell className="w-3.5 h-3.5" />
                  {t.turma_nome}
                </span>
              )}

              {t.professor_nome && (
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {t.professor_nome}
                </span>
              )}
            </div>
            {t.descricao && <p className="text-xs text-zinc-500 mt-2">{t.descricao}</p>}
          </div>

          <div className="shrink-0">
            {!encerrado && !t.confirmado && !inadimplente && podeConfirmar && (
              <Button size="sm" onClick={() => onConfirmar(t.id)} disabled={isLoading} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`btn-eu-vou-${t.id}`}>
                {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Eu vou</>}
              </Button>
            )}
            {!encerrado && !t.confirmado && inadimplente && (
              <Button size="sm" disabled className="bg-zinc-800 text-zinc-500 text-xs cursor-not-allowed" data-testid={`btn-eu-vou-blocked-${t.id}`}>
                <XCircle className="w-3.5 h-3.5 mr-1" /> Inapto
              </Button>
            )}
            {!encerrado && t.confirmado && (
              <Button size="sm" variant="outline" onClick={() => onCancelar(t.id)} disabled={isLoading} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid={`btn-cancelar-${t.id}`}>
                {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar</>}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
